import { useCallback, useEffect, useMemo, useState } from 'react';
import { ViewHeader } from '../components/ViewHeader';
import { searchTasks } from '../utils/searchTasks';
import { SCOPES } from '../scopes';
import { useTeam } from '../context/TeamContext';
import { AssigneeAvatar } from '../components/AssigneeAvatar';
import type { Task } from '../types';
import * as api from '../utils/apiClient';

function timeAgo(iso: string | undefined): string {
  if (!iso) return '';
  const secs = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function CompletedView() {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const { byId } = useTeam();

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setTasks(await api.listTasks('completed'));
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const filtered = useMemo(() => searchTasks(tasks, query), [tasks, query]);
  const sorted = useMemo(
    () => [...filtered].sort((a, b) => (b.completedAt ?? '').localeCompare(a.completedAt ?? '')),
    [filtered]
  );

  const onReopen = useCallback(async (task: Task) => {
    setBusyId(task.taskId);
    try {
      await api.updateTask(task.scope, task.taskId, { status: 'open' });
      setTasks((prev) => prev.filter((t) => t.taskId !== task.taskId));
    } finally {
      setBusyId(null);
    }
  }, []);

  const onDelete = useCallback(async (task: Task) => {
    setBusyId(task.taskId);
    try {
      await api.deleteTask(task.scope, task.taskId);
      setTasks((prev) => prev.filter((t) => t.taskId !== task.taskId));
    } finally {
      setBusyId(null);
    }
  }, []);

  return (
    <div className="scope-view">
      <ViewHeader title="Completed" searchValue={query} onSearchChange={setQuery} />
      <div className="pane-wrap">
        <section className="list-pane" aria-label="Completed tasks">
          {loading ? (
            <div className="muted">Loading…</div>
          ) : error ? (
            <div className="error">{error}</div>
          ) : sorted.length === 0 ? (
            <div className="task-list-empty">No completions in the last 30 days.</div>
          ) : (
            <div className="task-list" role="list">
              {sorted.map((task) => {
                const scopeColor = SCOPES.find((s) => s.id === task.scope)?.color ?? 'transparent';
                const busy = busyId === task.taskId;
                const assignee = byId(task.assigneeId ?? null);
                return (
                  <div
                    key={task.taskId}
                    className="task-row is-completed"
                    style={{ ['--row-accent' as string]: scopeColor }}
                    role="listitem"
                  >
                    <div />
                    <div className="task-row-body">
                      <div className="task-row-title-line">
                        <div className="task-row-title">
                          {assignee && <AssigneeAvatar member={assignee} size={18} />}
                          <span>{task.title}</span>
                        </div>
                        <span className="task-row-due">completed {timeAgo(task.completedAt)}</span>
                      </div>
                      <div className="trash-actions">
                        <button disabled={busy} onClick={() => void onReopen(task)}>
                          Reopen
                        </button>
                        <button className="danger" disabled={busy} onClick={() => void onDelete(task)}>
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
